import { h as __h } from "preact";
import { Component, h } from "preact";
/**
 * @import {
 *   AnyComponent,
 *   Attributes,
 *   ComponentChildren
 * } from "preact"
 */

import { SUPPORT_EMAIL } from "~/lib/consts.js";
import html from "~/util/html.js";
import { captureError } from "~/util/sentry.js";
import ErrorPage, { ErrorMessage } from "./ErrorPage.js";
import link from "~/style/link.js";

/**
 * @typedef {object} Props
 * @property {AnyComponent<{ error?: string }> | undefined} [fallback]
 * @property {ComponentChildren} [children]
 */

/** @extends {Component<Props, { error?: string }>} */
export default class ErrorBoundary extends Component {
  /** @override @type {{ error?: string }} */
  state = {};

  /**
   * @param {unknown} error
   * @override
   */
  static getDerivedStateFromError(error) {
    console.error(error);
    if (error instanceof Error) {
      captureError(error, "Error");
      return {
        error: error.message
      };
    }
    return {
      error: ""
    };
  }
  render() {
    if (!this.state.error) return this.props.children;
    const fallback = this.props.fallback ?? Fallback;
    return h(fallback, {
      error: this.state.error
    });
  }
}

/** @param {{ error?: string | undefined; message?: ComponentChildren }} props */
export function Fallback(props) {
  const message = props.message ?? __h("p", {}, "We're sorry, but Phaze hit an unexpected snag. Try ", " ", __h("button", {
    "class": link.link,
    onClick: () => location.reload()
  }, "restarting Phaze"), " ", " or ", " ", __h("a", {
    href: "mailto:" + SUPPORT_EMAIL
  }, "contacting support"), ".");
  return __h(ErrorPage, {
    title: "An error occurred",
    icon: "skull"
  }, message, props.error ? [__h("p", {}, "More details can be found below:"), __h(ErrorMessage, {
    message: props.error
  })] : null);
}

/**
 * A cache used to memoize the `errorBoundary` higher-order component wrapper function.
 *
 * @type {WeakMap<WeakKey, (props: any) => ComponentChildren>}
 */
const cache = new WeakMap();

/**
 * @template Props
 * @param {AnyComponent<Props>} component
 * @param {(props: { error?: string }) => ComponentChildren} [fallback]
 */
export function errorBoundary(component, fallback) {
  // if we've already wrapped the given component, return the wrapped component class
  const cached = cache.get(component);
  if (cached) return cached;

  /** @param {Props} props */
  function ProtectedComponent(props) {
    return __h(ErrorBoundary, {
      fallback: fallback
    }, h(component, /** @type {Attributes & Props} */props));
  }

  // cache the wrapped component class
  cache.set(component, ProtectedComponent);
  return ProtectedComponent;
}